import { useEffect, useRef } from "react";
import { useAtom, useAtomValue } from "@effect-atom/atom-react";
import { Effect, Fiber } from "effect";
import {
  xmlContentAtom,
  xslContentAtom,
  autoTransformEnabledAtom,
  isTransformingAtom,
  transformAtom,
} from "../atoms";

const DEBOUNCE_MS = 500;

export function useAutoTransform() {
  const xmlContent = useAtomValue(xmlContentAtom);
  const xslContent = useAtomValue(xslContentAtom);
  const autoTransformEnabled = useAtomValue(autoTransformEnabledAtom);
  const isTransforming = useAtomValue(isTransformingAtom);
  const [, transform] = useAtom(transformAtom);

  const pendingTransformRef = useRef(false);
  const isInitialMountRef = useRef(true);
  const fiberRef = useRef<Fiber.RuntimeFiber<void, never> | null>(null);
  // Use ref to access current isTransforming value without adding to deps
  const isTransformingRef = useRef(isTransforming);
  isTransformingRef.current = isTransforming;

  // Handle content changes with Effect-based debouncing
  useEffect(() => {
    if (isInitialMountRef.current) {
      isInitialMountRef.current = false;
      return;
    }

    if (!autoTransformEnabled) {
      return;
    }

    // Cancel any pending debounced transform
    if (fiberRef.current) {
      Effect.runFork(Fiber.interrupt(fiberRef.current));
      fiberRef.current = null;
    }

    // Create debounced transform effect
    const debouncedTransform = Effect.gen(function* () {
      yield* Effect.sleep(DEBOUNCE_MS);

      if (isTransformingRef.current) {
        pendingTransformRef.current = true;
      } else {
        transform(undefined);
      }
    });

    // Run the debounced effect
    fiberRef.current = Effect.runFork(debouncedTransform);

    return () => {
      if (fiberRef.current) {
        Effect.runFork(Fiber.interrupt(fiberRef.current));
        fiberRef.current = null;
      }
    };
  }, [xmlContent, xslContent, autoTransformEnabled, transform]);

  // Handle pending transform when current one completes
  useEffect(() => {
    if (!isTransforming && pendingTransformRef.current && autoTransformEnabled) {
      pendingTransformRef.current = false;
      transform(undefined);
    }
  }, [isTransforming, autoTransformEnabled, transform]);
}
